import { MutableRefObject, useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { SCROLLING_CONTAINER_ID } from '../../layout-constants';
import { useScrollToAnchorLink } from './use-scroll-to-anchor-link';

/**
 * Helper hooks to bring expected behavior to page changes and hashlinks. Uses hooks from
 * React Router, so won't work if placed outside a router.
 */
export function useScrollHelpers() {
  useScrollToTopOnPathChange();
  useScrollToAnchorLink();
}

export type ScrollToTopState = {
  scrollToTop?: boolean;
};
function useScrollToTopOnPathChange() {
  const location = useLocation();
  const state = location.state as ScrollToTopState | undefined;

  const element = document.getElementById(SCROLLING_CONTAINER_ID);

  // By default, scroll to top on any path change
  const scrollToTop = state?.scrollToTop ?? true;
  useEffect(() => {
    if (scrollToTop) {
      if (element) {
        element.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        // Fallback to window scroll when the platform's container element is not found (e.g., in facade)
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }, [element, location.pathname, scrollToTop]);
}

/**
 * Preserve the scroll offset of the layout scroll container across an unmount/remount of the
 * component that calls this hook — e.g. the DMA materiality list unmounts when a single matter
 * opens and remounts on "< List", and should return to the row the user left from.
 *
 * The offset lives in `offsetRef`, which the caller must own at a level that stays mounted across
 * the round-trip (for the DMA list that is the wrapper holding the list/detail routes). Pass a
 * null ref to disable entirely; the hook then no-ops, which keeps it inert in contexts that don't
 * opt in (e.g. the dma-v2 dashboard, whose scroll container differs).
 *
 * Two things are required for the restore to land on the right spot:
 * - The list must re-render at the same height on return (open branches restored, no
 *   virtualization), so a saved offset maps back to the same row.
 * - `isReady` must be false until the height-determining data is present; restoring against a
 *   short loading skeleton would clamp the offset to the top.
 *
 * The caller must also suppress useScrollToTopOnPathChange for the return navigation (pass
 * `state={{ scrollToTop: false }}` on the back link), otherwise the smooth scroll-to-top above
 * runs on that path change and stomps the restore.
 */
export function useScrollRestoration(
  offsetRef: MutableRefObject<number> | null,
  isReady: boolean,
  containerId: string = SCROLLING_CONTAINER_ID,
) {
  const hasRestoredRef = useRef(false);

  // Restore once per mount, after the height-determining data is present.
  useLayoutEffect(() => {
    if (!offsetRef || !isReady || hasRestoredRef.current) {
      return;
    }
    hasRestoredRef.current = true;
    const container = document.getElementById(containerId);
    if (container && offsetRef.current > 0) {
      container.scrollTop = offsetRef.current;
    }
  }, [offsetRef, isReady, containerId]);

  // Save on unmount, reading the live position off the container (an ancestor that outlives this
  // component). This runs as a layout-effect cleanup, i.e. before the passive scroll-to-top effect
  // fires for the outgoing navigation, so it captures the pre-scroll position.
  useLayoutEffect(() => {
    if (!offsetRef) {
      return;
    }
    return () => {
      const container = document.getElementById(containerId);
      if (container) {
        offsetRef.current = container.scrollTop;
      }
    };
  }, [offsetRef, containerId]);
}
